import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, Check, Image as ImageIcon, Video, Music, Code, PenTool, Palette, Zap, Search, Sparkles, UploadCloud } from 'lucide-react';
import { GlassCard } from './ui/GlassCard';
import { supabase } from '../lib/supabase';
import { PricingTier } from '../types/database';
interface AdminAddToolModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const CATEGORIES = [{
  name: 'Image',
  icon: ImageIcon
}, {
  name: 'Video',
  icon: Video
}, {
  name: 'Audio',
  icon: Music
}, {
  name: 'Development',
  icon: Code
}, {
  name: 'Writing',
  icon: PenTool
}, {
  name: 'Design',
  icon: Palette
}, {
  name: 'Productivity',
  icon: Zap
}, {
  name: 'Research',
  icon: Search
}, {
  name: 'Other',
  icon: Sparkles
}];
export function AdminAddToolModal({
  isOpen,
  onClose
}: AdminAddToolModalProps) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website_url: '',
    category: 'Productivity',
    logo_url: ''
  });
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([{
    name: 'Free',
    price: '0',
    features: ['Basic features']
  }]);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setStep(1);
      setPreviewUrl(null);
      setFormData({
        name: '',
        description: '',
        website_url: '',
        category: 'Productivity',
        logo_url: ''
      });
      setPricingTiers([{
        name: 'Free',
        price: '0',
        features: ['Basic features']
      }]);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };
  const handleFile = (file: File) => {
    if (!file.type.match('image.*')) {
      alert('Please upload an image file (JPG, PNG, SVG)');
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setFormData({
      ...formData,
      logo_url: url
    });
  };
  const removeFile = () => {
    setPreviewUrl(null);
    setFormData({
      ...formData,
      logo_url: ''
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const addPricingTier = () => {
    setPricingTiers([...pricingTiers, {
      name: '',
      price: '',
      features: ['']
    }]);
  };
  const removePricingTier = (index: number) => {
    setPricingTiers(pricingTiers.filter((_, i) => i !== index));
  };
  const updatePricingTier = (index: number, field: keyof PricingTier, value: any) => {
    const newTiers = [...pricingTiers];
    newTiers[index] = {
      ...newTiers[index],
      [field]: value
    };
    setPricingTiers(newTiers);
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Admin adds directly to tools table (no approval needed)
      const {
        error
      } = await supabase.from('tools').insert({
        name: formData.name,
        description: formData.description,
        logo_url: formData.logo_url,
        website_url: formData.website_url,
        category: formData.category,
        pricing_tiers: pricingTiers,
        is_featured: false,
        featured_span: '1x1'
      });
      if (error) throw error;
      setStep(3);
    } catch (err) {
      console.error('Error adding tool:', err);
      alert('Failed to add tool. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const SelectedCategoryIcon = CATEGORIES.find(c => c.name === formData.category)?.icon || Sparkles;
  if (!isOpen) return null;
  const modalContent = <AnimatePresence data-id="element-23">
      <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" data-id="element-24">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/90 backdrop-blur-xl" onClick={onClose} data-id="element-25" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-26">
          <GlassCard className="relative border-white/10" data-id="element-27">
            <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors z-10" data-id="element-28">
              <X size={20} data-id="element-29" />
            </button>

            <div className="p-8" data-id="element-30">
              {step === 1 && <div className="space-y-6" data-id="element-31">
                  <div data-id="element-32">
                    <h2 className="text-3xl font-bold text-white mb-2 font-special" data-id="element-33">
                      Add Tool (Admin)
                    </h2>
                    <p className="text-gray-400" data-id="element-34">
                      Add a tool directly - no approval needed.
                    </p>
                  </div>

                  <div className="space-y-5" data-id="element-35">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-36">
                      <div data-id="element-37">
                        <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-38">
                          Tool Name
                        </label>
                        <input type="text" name="name" value={formData.name} onChange={handleInputChange} required className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 focus:outline-none transition-all" placeholder="e.g. ChatGPT" data-id="element-39" />
                      </div>
                      <div data-id="element-40">
                        <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-41">
                          Website URL
                        </label>
                        <input type="url" name="website_url" value={formData.website_url} onChange={handleInputChange} required className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 focus:outline-none transition-all" placeholder="https://..." data-id="element-42" />
                      </div>
                    </div>

                    <div data-id="element-43">
                      <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-44">
                        Description
                      </label>
                      <textarea name="description" value={formData.description} onChange={handleInputChange} required rows={3} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 focus:outline-none transition-all resize-none" placeholder="What does this tool do?" data-id="element-45" />
                    </div>

                    <div data-id="element-46">
                      <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-47">
                        Category
                      </label>
                      <div className="relative" data-id="element-48">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-400 pointer-events-none" data-id="element-49">
                          <SelectedCategoryIcon size={18} data-id="element-50" />
                        </div>
                        <select name="category" value={formData.category} onChange={handleInputChange} required className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 focus:outline-none transition-all appearance-none cursor-pointer" data-id="element-51">
                          {CATEGORIES.map(cat => <option key={cat.name} value={cat.name} className="bg-gray-900 text-white" data-id="element-52">
                              {cat.name}
                            </option>)}
                        </select>
                      </div>
                    </div>

                    <div data-id="element-53">
                      <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-54">
                        Logo
                      </label>
                      <div className={`relative border-2 border-dashed rounded-xl p-6 transition-all text-center cursor-pointer ${dragActive ? 'border-cyan-500 bg-cyan-500/10' : 'border-white/10 bg-white/5 hover:border-white/20'}`} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} onClick={() => !previewUrl && fileInputRef.current?.click()} data-id="element-55">
                        <input ref={fileInputRef} type="file" className="hidden" accept="image/png, image/jpeg, image/svg+xml" onChange={handleFileSelect} required data-id="element-56" />

                        {previewUrl ? <div className="relative flex items-center justify-center" data-id="element-57">
                            <div className="w-20 h-20 rounded-xl overflow-hidden bg-black/20 border border-white/10" data-id="element-58">
                              <img src={previewUrl} alt="Preview" className="w-full h-full object-contain" data-id="element-59" />
                            </div>
                            <button onClick={e => {
                        e.stopPropagation();
                        removeFile();
                      }} className="absolute -top-2 -right-2 p-1.5 rounded-full bg-red-500 text-white hover:bg-red-600" data-id="element-60">
                              <Trash2 size={14} data-id="element-61" />
                            </button>
                          </div> : <div className="flex flex-col items-center gap-2" data-id="element-62">
                            <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center" data-id="element-63">
                              <UploadCloud size={24} className="text-gray-400" data-id="element-64" />
                            </div>
                            <p className="text-sm font-medium text-white" data-id="element-65">
                              Click to upload or drag and drop
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-66">
                              SVG, PNG, JPG
                            </p>
                          </div>}
                      </div>
                    </div>

                    <div className="pt-4 flex justify-end" data-id="element-67">
                      <button onClick={() => setStep(2)} className="px-8 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-bold hover:opacity-90 transition-all" data-id="element-68">
                        Next: Pricing
                      </button>
                    </div>
                  </div>
                </div>}

              {step === 2 && <div className="space-y-6" data-id="element-69">
                  <div data-id="element-70">
                    <h2 className="text-3xl font-bold text-white mb-2 font-special" data-id="element-71">
                      Pricing Tiers
                    </h2>
                    <p className="text-gray-400" data-id="element-72">
                      How much does your tool cost?
                    </p>
                  </div>

                  <div className="space-y-4" data-id="element-73">
                    {pricingTiers.map((tier, index) => <div key={index} className="p-4 rounded-xl bg-white/5 border border-white/10 relative" data-id="element-74">
                        {pricingTiers.length > 1 && <button onClick={() => removePricingTier(index)} className="absolute top-2 right-2 p-2 text-gray-500 hover:text-red-400" data-id="element-75">
                            <Trash2 size={16} data-id="element-76" />
                          </button>}

                        <div className="grid grid-cols-2 gap-4" data-id="element-77">
                          <div data-id="element-78">
                            <label className="block text-xs text-gray-400 mb-1" data-id="element-79">
                              Plan Name
                            </label>
                            <input type="text" value={tier.name} onChange={e => updatePricingTier(index, 'name', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none" placeholder="e.g. Pro" data-id="element-80" />
                          </div>
                          <div data-id="element-81">
                            <label className="block text-xs text-gray-400 mb-1" data-id="element-82">
                              Price
                            </label>
                            <input type="text" value={tier.price} onChange={e => updatePricingTier(index, 'price', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none" placeholder="e.g. $10/mo" data-id="element-83" />
                          </div>
                        </div>
                      </div>)}

                    <button onClick={addPricingTier} className="w-full py-4 rounded-xl border border-dashed border-white/20 text-gray-400 hover:text-white hover:border-cyan-500/50 transition-all flex items-center justify-center gap-2" data-id="element-84">
                      <Plus size={16} data-id="element-85" />
                      <span data-id="element-86">Add Another Tier</span>
                    </button>
                  </div>

                  <div className="flex justify-between pt-4 border-t border-white/10" data-id="element-87">
                    <button onClick={() => setStep(1)} className="px-6 py-3 rounded-xl text-gray-400 hover:text-white hover:bg-white/5 transition-all" data-id="element-88">
                      Back
                    </button>
                    <button onClick={handleSubmit} disabled={isSubmitting} className="px-8 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-bold hover:opacity-90 transition-all disabled:opacity-50 flex items-center gap-2" data-id="element-89">
                      {isSubmitting ? <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-90" />
                          <span data-id="element-91">Adding...</span>
                        </> : <>
                          <span data-id="element-92">Add Tool</span>
                          <Check size={18} data-id="element-93" />
                        </>}
                    </button>
                  </div>
                </div>}

              {step === 3 && <div className="text-center py-12" data-id="element-94">
                  <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-green-500/30" data-id="element-95">
                    <Check size={48} className="text-green-400" data-id="element-96" />
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-3 font-special" data-id="element-97">
                    Tool Added Successfully!
                  </h2>
                  <p className="text-gray-400 mb-8" data-id="element-98">
                    <span className="text-white font-bold" data-id="element-99">
                      {formData.name}
                    </span>{' '}
                    has been added to your tools directory.
                  </p>
                  <button onClick={onClose} className="px-10 py-3 rounded-xl bg-white text-black font-bold hover:bg-gray-200 transition-all" data-id="element-100">
                    Close
                  </button>
                </div>}
            </div>
          </GlassCard>
        </motion.div>
      </div>
    </AnimatePresence>;
  return createPortal(modalContent, document.body);
}